import { Typography, styled } from '@mui/material';
import parse from 'html-react-parser';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetDashboards } from 'routes/Support/components/TransferDashboardUser/hooks/dashboards';
import Flex from 'ui/Flex/Flex';
import CurrentHomePageChip from './CurrentHomePageChip';
import DashboardSelectionModal from './DashboardSelectionModal';
import { dashboardSlugToUrl, getCurrentHomePage } from './helpers/homePageUrl';
import { HomePageMetadata } from './helpers/types';
import { useUpdateClientHomePage } from './hooks/updateHomePageUrl';

const StyDecisionConfigurationItem = styled(Flex, {
    name: 'StyDecisionConfigurationItem',
})(({ theme }) => ({
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
}));

type HomePageUrlFormProps = {
    bind: string;
    homePageUrl: string | null;
};

const HomePageUrlForm = ({ bind, homePageUrl }: HomePageUrlFormProps) => {
    const { t } = useTranslation();
    const [homePageMetadata, setHomePageMetadata] = useState<HomePageMetadata>({
        status: 'loading',
        isDashboard: false,
        homePageUrl: null,
    });

    const [open, setOpen] = useState(false);

    const { data: bindDashboards } = useGetDashboards(bind);

    const onUpdateSuccess = (client: Pick<ClientT, 'homePageUrl'>) => {
        const currentHomePageStatus = getCurrentHomePage(
            client.homePageUrl,
            bindDashboards ?? [],
        );
        setHomePageMetadata(currentHomePageStatus);
    };

    const updateHomePageUrl = useUpdateClientHomePage(onUpdateSuccess);

    const onDashboardSelected = (slug: string | null) => {
        if (!bind) return;
        const newHomePageUrl = slug ? dashboardSlugToUrl(slug) : null;
        if (newHomePageUrl === homePageMetadata.homePageUrl) return;
        updateHomePageUrl.mutate({
            bind,
            homePageUrl: newHomePageUrl,
        });
    };

    useEffect(() => {
        if (bindDashboards) {
            const currentHomePageStatus = getCurrentHomePage(
                homePageUrl,
                bindDashboards,
            );
            setHomePageMetadata(currentHomePageStatus);
        }
    }, [bindDashboards, homePageUrl]);
    return (
        <StyDecisionConfigurationItem>
            <Typography variant="h4">
                {t('support.decisionConfiguration.decisionHomePage.title')}
            </Typography>
            <Typography variant="body1">
                {parse(
                    t(
                        'support.decisionConfiguration.decisionHomePage.description',
                    ),
                )}
            </Typography>
            <Flex alignItems="center" gap={1}>
                <Typography variant="h5">
                    {t(
                        'support.decisionConfiguration.decisionHomePage.currentHomePage',
                    )}
                </Typography>
                <CurrentHomePageChip
                    bind={bind}
                    homePageMetadata={homePageMetadata}
                    onActionClick={() => setOpen(true)}
                />
                {open && (
                    <DashboardSelectionModal
                        open={open}
                        dashboards={bindDashboards ?? []}
                        onClose={() => setOpen(false)}
                        onClick={onDashboardSelected}
                    />
                )}
            </Flex>
        </StyDecisionConfigurationItem>
    );
};

export default HomePageUrlForm;
